import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ProviderConnState } from '@apis/auth';
import type { ProviderName } from '@interfaces/IAuth';

/**
 * A per-provider connection issue, surfaced so a global control (the TopBar
 * reconnect button) can prompt the user without each page wiring its own banner.
 * Set from three sources: reactively on any provider-backed query failure (the
 * QueryClient `onError` → `providerIssueAction`), the TopBar's Genba expiry
 * timer, and the one-shot `/api/auth/providers` status fetch on load. Cleared
 * when the provider is (re)connected (AuthContext).
 *
 * The non-`ok` subset of `ProviderConnState` — derived so the two can't drift.
 */
export type ProviderConnIssue = Exclude<ProviderConnState, 'ok'>;

export interface IProviderStatusState {
  /** Per-provider issue, or `null` when connected / no known problem. */
  genba: ProviderConnIssue | null;
  trusstor: ProviderConnIssue | null;
}

const initialState: IProviderStatusState = { genba: null, trusstor: null };

const providerStatusSlice = createSlice({
  name: 'providerStatus',
  initialState,
  reducers: {
    setProviderIssue(
      state,
      action: PayloadAction<{ provider: ProviderName; issue: ProviderConnIssue }>,
    ) {
      state[action.payload.provider] = action.payload.issue;
    },
    clearProviderIssue(state, action: PayloadAction<ProviderName>) {
      state[action.payload] = null;
    },
  },
});

export const { setProviderIssue, clearProviderIssue } = providerStatusSlice.actions;
export const providerStatusReducer = providerStatusSlice.reducer;
